import { ContactSyncEnterpriseConfig } from './contact_sync_enterprise_config';
import { ContactSyncEnterpriseNew } from './contact_sync_enterprise_new';
import { writeContactsToLoki, writeOrgsToLoki, writeOrgContactsToLoki } from './contact_sync_enterprise_loki';
import { contactSearchDbName } from './util/const';
const ENABLE_HAS_ALL_POLLING = false;
export class EnterpriseSyncCoordinator {
    constructor(ctx, syncIntervalUpdater, onSyncTrigger) {
        this.configMap = new Map();
        this.syncIntervalUpdater = null;
        this.onSyncTrigger = null;
        this.ctx = ctx;
        this.syncIntervalUpdater = syncIntervalUpdater !== null && syncIntervalUpdater !== void 0 ? syncIntervalUpdater : null;
        this.onSyncTrigger = onSyncTrigger !== null && onSyncTrigger !== void 0 ? onSyncTrigger : null;
        this.syncModule = new ContactSyncEnterpriseNew();
        this.syncModule.init({
            systemApi: ctx.systemApi,
            lokiApi: ctx.lokiApi,
            contactServer: ctx.contactServer,
            contactDB: ctx.contactDB,
            tracker: ctx.tracker,
        });
    }
    async getOrCreateConfig(_account, domain) {
        var _a, _b;
        const key = `${_account}::${domain}`;
        const existing = this.configMap.get(key);
        if (existing) {
            return { config: existing, isNew: false };
        }
        const config = new ContactSyncEnterpriseConfig();
        await config.init({
            systemApi: this.ctx.systemApi,
            http: this.ctx.http,
            storeApi: this.ctx.storeApi,
        }, _account, domain, (_a = this.syncIntervalUpdater) !== null && _a !== void 0 ? _a : undefined, (_b = this.onSyncTrigger) !== null && _b !== void 0 ? _b : undefined);
        this.configMap.set(key, config);
        return { config, isNew: true };
    }
    async sync(options) {
        var _a;
        const { force = false, _account, sessionId, enablePolling = true } = options;
        const tracker = this.ctx.tracker;
        const mainDomain = this.getMainDomain(_account);
        if (!mainDomain) {
            console.warn('[EnterpriseSyncCoordinator] no main domain found, skip sync');
            return [];
        }
        const { config, isNew } = await this.getOrCreateConfig(_account, mainDomain);
        if (force && config.isPolling) {
            config.stopPolling();
        }
        if (!isNew || force) {
            await config.fetchConfig(force);
        }
        if (sessionId) {
            tracker === null || tracker === void 0 ? void 0 : tracker.recordTask(sessionId, { type: 'enterprise', domain: mainDomain, phase: 'fetch' });
        }
        const result = await this.syncOneDomain({ domain: mainDomain, _account, sessionId }).catch(ex => {
            console.error(`[EnterpriseSyncCoordinator] syncOneDomain failed: ${mainDomain}`, ex);
            return null;
        });
        const hasAllContacts = (_a = config.getConfig()) === null || _a === void 0 ? void 0 : _a.hasAllContacts;
        const applyPolling = () => {
            if (!ENABLE_HAS_ALL_POLLING) {
                if (enablePolling && !config.isPolling) {
                    config.startPolling();
                }
                return;
            }
            if (hasAllContacts === 1) {
                if (config.isPolling) {
                    config.stopPolling();
                }
            }
            else if (hasAllContacts === 0 && enablePolling && !config.isPolling) {
                config.startPolling();
            }
        };
        if (result) {
            const wrappedPromise = result.dexiePromise.then(() => applyPolling());
            return [wrappedPromise];
        }
        applyPolling();
        return [];
    }
    async syncOneDomain(params) {
        const { domain, _account, sessionId } = params;
        const { config } = await this.getOrCreateConfig(_account, domain);
        const configData = config.getConfig();
        if (!configData) {
            console.warn(`[EnterpriseSyncCoordinator] no config data for domain=${domain}, skip`);
            return null;
        }
        const { needSync } = configData;
        if (needSync === 0) {
            await this.handleNeedSyncZero(_account);
            return null;
        }
        else {
            const { hasAllContacts, lastUpdateTime, dexiePromise } = await this.syncModule.doSync(domain, _account, sessionId);
            if (hasAllContacts !== undefined) {
                await config.setHasAllContacts(hasAllContacts);
            }
            if (lastUpdateTime !== undefined) {
                await config.setLastUpdateTime(lastUpdateTime);
            }
            return { dexiePromise };
        }
    }
    async handleNeedSyncZero(_account) {
        const dbAccount = this.getDbAccount(_account);
        const dbName = this.ctx.contactUtil.contactDbName;
        const [hasLokiContactData, hasLokiOrgData] = await Promise.all([this.checkLokiHasEnterpriseData(_account), this.checkLokiHasOrgData(_account)]);
        if (hasLokiContactData && hasLokiOrgData) {
            console.log('[EnterpriseSyncCoordinator] needSync=0, loki has org+contact data, skip rebuild.');
            return;
        }
        console.log(`[EnterpriseSyncCoordinator] needSync=0, loki missing data (org=${hasLokiOrgData}, contact=${hasLokiContactData}), rebuilding.`);
        if (!hasLokiOrgData) {
            await this.rebuildOrgsFromDexie(dbName, dbAccount);
        }
        if (!hasLokiContactData) {
            await this.rebuildContactsFromDexie(dbName, dbAccount);
        }
    }
    async checkLokiHasEnterpriseData(_account) {
        try {
            const dbAccount = this.getDbAccount(_account);
            const results = await this.ctx.lokiApi.getByRangeCondition({
                dbName: contactSearchDbName,
                tableName: 'contact',
                adCondition: {
                    type: 'equals',
                    field: 'type',
                    args: ['enterprise'],
                },
                count: 1,
                _dbAccount: dbAccount,
            });
            return Array.isArray(results) && results.length > 0;
        }
        catch (ex) {
            console.error('[EnterpriseSyncCoordinator] checkLokiHasEnterpriseData error', ex);
            return false;
        }
    }
    async checkLokiHasOrgData(_account) {
        try {
            const dbAccount = this.getDbAccount(_account);
            const results = await this.ctx.lokiApi.getByRangeCondition({
                dbName: contactSearchDbName,
                tableName: 'org',
                adCondition: {
                    type: 'below',
                    field: 'type',
                    args: [2000],
                },
                count: 1,
                _dbAccount: dbAccount,
            });
            return Array.isArray(results) && results.length > 0;
        }
        catch (ex) {
            console.error('[EnterpriseSyncCoordinator] checkLokiHasOrgData error', ex);
            return false;
        }
    }
    async rebuildOrgsFromDexie(dbName, dbAccount) {
        const orgs = await this.ctx.dbApi.getByRangeCondition({
            dbName,
            tableName: this.ctx.contactUtil.orgTable,
            adCondition: {
                type: 'below',
                field: 'type',
                args: [2000],
            },
            _dbAccount: dbAccount,
        });
        await writeOrgsToLoki(this.ctx.lokiApi, orgs, dbAccount);
        console.log(`[EnterpriseSyncCoordinator] rebuildOrgsFromDexie done. orgs=${orgs.length}`);
    }
    async rebuildContactsFromDexie(dbName, dbAccount) {
        const contacts = await this.ctx.dbApi.getByRangeCondition({
            dbName,
            tableName: this.ctx.contactUtil.contactTable,
            adCondition: {
                type: 'equals',
                field: 'type',
                args: ['enterprise'],
            },
            _dbAccount: dbAccount,
        });
        const orgContacts = await this.ctx.dbApi.getByRangeCondition({
            dbName,
            tableName: this.ctx.contactUtil.orgContactTable,
            adCondition: {
                type: 'equals',
                field: 'type',
                args: ['enterprise'],
            },
            _dbAccount: dbAccount,
        });
        let contactItems = [];
        if (contacts.length > 0) {
            const contactIds = contacts.map(c => c.id);
            contactItems = await this.ctx.dbApi.getByRangeCondition({
                dbName,
                tableName: this.ctx.contactUtil.contactItemTable,
                adCondition: {
                    type: 'anyOf',
                    field: 'contactId',
                    args: [contactIds],
                },
                _dbAccount: dbAccount,
            });
        }
        await Promise.all([writeContactsToLoki(this.ctx.lokiApi, contacts, contactItems, dbAccount), writeOrgContactsToLoki(this.ctx.lokiApi, orgContacts, dbAccount)]);
        console.log(`[EnterpriseSyncCoordinator] rebuildContactsFromDexie done. contacts=${contacts.length}, orgContacts=${orgContacts.length}`);
    }
    getDbAccount(_account) {
        const user = this.ctx.systemApi.getCurrentUser(_account);
        return (user === null || user === void 0 ? void 0 : user.accountMd5) || _account;
    }
    getMainDomain(_account) {
        const user = this.ctx.systemApi.getCurrentUser(_account);
        return user === null || user === void 0 ? void 0 : user.domain;
    }
    getConfig(_account) {
        var _a;
        if (_account) {
            for (const [key, config] of this.configMap) {
                if (key.startsWith(`${_account}::`)) {
                    return config.getConfig();
                }
            }
            return null;
        }
        const first = this.configMap.values().next().value;
        return (_a = first === null || first === void 0 ? void 0 : first.getConfig()) !== null && _a !== void 0 ? _a : null;
    }
    destroy(_account) {
        for (const [key, config] of this.configMap) {
            if (key.startsWith(`${_account}::`)) {
                config.destroy();
                this.configMap.delete(key);
            }
        }
        console.log(`[EnterpriseSyncCoordinator] destroy done for account=${_account}`);
    }
    clearConfigStores(_account) {
        if (!this.configMap || this.configMap.size === 0) {
            return;
        }
        for (const [key, config] of this.configMap) {
            if (key.startsWith(`${_account}::`)) {
                try {
                    config.clearStore();
                }
                catch (ex) {
                    console.error(`[EnterpriseSyncCoordinator] clearConfigStores failed for key=${key}`, ex);
                }
            }
        }
        console.log(`[EnterpriseSyncCoordinator] clearConfigStores done for account=${_account}`);
    }
    destroyAll() {
        for (const config of this.configMap.values()) {
            config.destroy();
        }
        this.configMap.clear();
        this.syncModule.destroy();
        this.syncIntervalUpdater = null;
        this.onSyncTrigger = null;
        console.log('[EnterpriseSyncCoordinator] destroyAll done');
    }
}
